import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePortalQuery } from '../../hooks/usePortalQuery';
import { listProjects } from '../../lib/portalApi';
import { can } from '../../lib/portalPermissions';
import { usePortalAuth } from '../../context/PortalAuth';
import { orgName } from '../../context/PortalOrg';
import { ORGANISATIONS } from '../../data/portal';
import { PageHeader } from '../../components/portal/PageHeader';
import { ProductTag } from '../../components/portal/ProductTag';
import { StatusBadge } from '../../components/portal/StatusBadge';
import { EmptyState, QueryState } from '../../components/portal/QueryState';
import { formatDate, PROJECT_STATUS_LABELS } from '../../utils/portalFormat';
import type { ProjectStatus } from '../../types/portal';
const STATUSES: ProjectStatus[] = ['active', 'paused', 'completed', 'archived'];
export function PortalProjects() {
  const {
    orgId = ''
  } = useParams<{
    orgId: string;
  }>();
  const {
    roleIn
  } = usePortalAuth();
  const role = roleIn(orgId);
  const [status, setStatus] = React.useState<ProjectStatus | ''>('');
  const [productId, setProductId] = React.useState('');
  const [page, setPage] = React.useState(1);
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => listProjects(orgId, {
    status: status || undefined,
    productId: productId || undefined,
    page,
    limit: 10
  }), [orgId, status, productId, page]);
  const org = ORGANISATIONS.find(o => o.id === orgId);
  return <div data-id="element-3194" data-mp="2rv29esyw5jmw">
      <PageHeader eyebrow={orgName(orgId)} title="Projects" description={can(role, 'projects.viewAll') ? 'Every project in the organisation.' : 'The projects you have been assigned to. An admin or owner can add you to others.'} data-id="element-3195" data-mp="2rushv0y774f3" />

      <div className="mt-6 flex flex-wrap gap-3" data-id="element-3196" data-mp="16jsi3wt3j896">
        <label className="sr-only" htmlFor="filter-status" data-id="element-3197" data-mp="16kleq4ycchdl">
          Status
        </label>
        <select id="filter-status" value={status} onChange={e => {
        setStatus(e.target.value as ProjectStatus | '');
        setPage(1);
      }} className="h-10 rounded-sm border border-line bg-charcoal px-3 text-[14px] text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-3198" data-mp="11jxpnotoaam0">
          <option value="" data-id="element-3199" data-mp="3krb8wq5jv9lr">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s} data-id="element-3200" data-mp="362oucqnqylvl">
              {PROJECT_STATUS_LABELS[s]}
            </option>)}
        </select>
        {org && org.productIds.length > 1 && <>
            <label className="sr-only" htmlFor="filter-product" data-id="element-3201" data-mp="3av893qy67n1z">
              Brand
            </label>
            <select id="filter-product" value={productId} onChange={e => {
          setProductId(e.target.value);
          setPage(1);
        }} className="h-10 rounded-sm border border-line bg-charcoal px-3 text-[14px] text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-3202" data-mp="32138wgx5cz9s">
              <option value="" data-id="element-3203" data-mp="28j6ts6e363kb">All brands</option>
              {org.productIds.map(id => <option key={id} value={id} data-id="element-3204" data-mp="24hlkev0rg7ev">
                  {id === 'prod-gov' ? 'Cavalla Gov' : 'Cavalla Solution'}
                </option>)}
            </select>
          </>}
      </div>

      <div className="mt-6" data-id="element-3205" data-mp="2rfyiivcedavr">
        <QueryState loading={loading} error={error} data-id="element-3206" data-mp="2rf6m5iaz794f">
          {data && data.data.length === 0 ? <EmptyState title="No projects match" body={status || productId ? 'Try clearing the filters.' : 'You have not been added to a project in this organisation yet.'} data-id="element-3207" data-mp="2nf66xvg1ytwb" /> : data && <>
                <div className="overflow-hidden rounded-md border border-line" data-id="element-3208" data-mp="2w7gjvkic1dh9">
                  <table className="w-full text-left" data-id="element-3209" data-mp="3b4qrujvro188">
                    <thead className="hidden bg-panel text-[12px] uppercase tracking-[0.12em] text-muted sm:table-header-group" data-id="element-3210" data-mp="364ul4cvopvoz">
                      <tr data-id="element-3211" data-mp="13h5ui4z1fjjs">
                        <th scope="col" className="px-5 py-3 font-medium" data-id="element-3212" data-mp="13hnwzjcamu05">Project</th>
                        <th scope="col" className="px-5 py-3 font-medium" data-id="element-3213" data-mp="18fz77v6i36iw">Dates</th>
                        <th scope="col" className="px-5 py-3 font-medium" data-id="element-3214" data-mp="1d91trjcv1fpl">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line" data-id="element-3215" data-mp="1i96btdtf2ihx">
                      {data.data.map(p => <tr key={p.id} className="group relative block transition-colors duration-150 hover:bg-panel/60 sm:table-row" data-id="element-3216" data-mp="klpi4488msb2">
                          <td className="block px-5 pt-4 sm:table-cell sm:py-4" data-id="element-3217" data-mp="ugzhslb5ynlo">
                            <ProductTag productId={p.productId} data-id="element-3218" data-mp="zgc6zcpic6rf" />
                            <Link to={`/portal/orgs/${orgId}/projects/${p.id}`} className="mt-1 block text-[16px] text-ink after:absolute after:inset-0 group-hover:text-royal" data-id="element-3219" data-mp="zhwyetgiz9l2">
                              {p.name}
                            </Link>
                          </td>
                          <td className="block px-5 pt-1 text-[14px] text-muted sm:table-cell sm:py-4" data-id="element-3220" data-mp="3vda0hnrle1a3">
                            {formatDate(p.startDate)} to {formatDate(p.endDate)}
                          </td>
                          <td className="block px-5 pb-4 pt-2 sm:table-cell sm:py-4" data-id="element-3221" data-mp="3lm5omwx7qwjh">
                            <StatusBadge kind="project" status={p.status} data-id="element-3222" data-mp="3godtzv56owbl" />
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                {data.meta.totalPages > 1 && <nav aria-label="Pagination" className="mt-5 flex items-center justify-between text-[14px] text-muted" data-id="element-3223" data-mp="32q73x16h80mh">
                    <span data-id="element-3224" data-mp="2xrvuuso09u5b">
                      Page {data.meta.page} of {data.meta.totalPages}, {data.meta.total} projects
                    </span>
                    <span className="flex gap-2" data-id="element-3225" data-mp="vy6dg5z1u43l">
                      <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-sm border border-line px-3 py-1.5 text-ink disabled:opacity-40" data-id="element-3226" data-mp="vzhdpvtactpy">
                        Previous
                      </button>
                      <button type="button" disabled={page >= data.meta.totalPages} onClick={() => setPage(page + 1)} className="rounded-sm border border-line px-3 py-1.5 text-ink disabled:opacity-40" data-id="element-3227" data-mp="2gh8r162enltz">
                        Next
                      </button>
                    </span>
                  </nav>}
              </>}
        </QueryState>
      </div>
    </div>;
}